import { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import Drawer from '../common/Drawer/Drawer'
import DrawerAvatar from '../common/Drawer/DrawerAvatar'
import DrawerFacts from '../common/Drawer/DrawerFacts'
import DrawerLink from '../common/Drawer/DrawerLink'
import DrawerSection from '../common/Drawer/DrawerSection'
import DrawerCard from '../common/Drawer/DrawerCard'
import DrawerEmptyState from '../common/Drawer/DrawerEmptyState'
import ConfirmationModal2 from '../common/ConfirmationModal2'
import { deleteOrganization } from '../../actions/organizationsActions'
import { initialsOf } from '../../utility/helperFunctions'
import { ReactComponent as WarningIcon } from '../../assets/images/common/icons/warning-icon.svg'

import classes from './OrganizationDrawer.module.scss'

const OrganizationDrawer = ({ organization, onClose, onEdit }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = () => {
    setIsDeleting(true)
    dispatch(deleteOrganization(organization.id)).unwrap()
      .then(() => {
        setShowDeleteModal(false)
        toast.success(t('organizations.actions.deleteSuccess'), { position: toast.POSITION.TOP_RIGHT })
        onClose()
      })
      .catch(() => toast.error(t('organizations.actions.deleteError'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setIsDeleting(false))
  }

  const detachedItems = [
    organization.coachesCount > 0 && t('organizations.actions.coachesCount', { count: organization.coachesCount }),
    organization.communitiesCount > 0 && t('organizations.actions.communitiesCount', { count: organization.communitiesCount }),
  ].filter(Boolean)

  const facts = [
    { label: t('organizations.table.coaches'), value: organization.coachesCount },
    { label: t('organizations.table.certifiedCoaches'), value: organization.certifiedCoachesCount },
    { label: t('organizations.table.communities'), value: organization.communitiesCount },
  ]

  return (
    <Drawer
      onClose={onClose}
      avatar={<DrawerAvatar icon='fa-users' />}
      title={organization.name}
      subtitle={t('organizations.drawer.createdOn', { date: moment(organization.createdAt).format('MMM D, YYYY') })}
    >
      <DrawerFacts facts={facts} />

      <DrawerLink to={`/coaches?organization_id=${organization.id}`} icon='fa-user' external>{t('organizations.drawer.viewCoaches')}</DrawerLink>

      <DrawerSection title={t('organizations.table.communities')} hint={t('organizations.drawer.communitiesHint')}>
        {organization.communities?.length ? organization.communities.map(community => (
          <DrawerCard
            key={community.id}
            icon={initialsOf(community.name)}
            image={community.photo?.smallFileUrl}
            iconVariant='avatar'
            label={community.name}
            to={`/communities/${community.id}`}
          />
        )) : (
          <DrawerEmptyState>{t('organizations.drawer.noCommunities')}</DrawerEmptyState>
        )}
      </DrawerSection>

      <DrawerSection title={t('organizations.drawer.actions')}>
        <DrawerCard icon={<i className='fa fa-pencil' aria-hidden='true' />} label={t('common.edit')} onClick={onEdit} />
        <DrawerCard
          icon={<i className='fa fa-trash-o' aria-hidden='true' />}
          label={t('common.delete')}
          onClick={() => setShowDeleteModal(true)}
          danger
        />
      </DrawerSection>

      <ConfirmationModal2
        showModal={showDeleteModal}
        closeModal={() => setShowDeleteModal(false)}
        onDeny={() => setShowDeleteModal(false)}
        denyMessage={t('common.cancel')}
        onProceed={handleDelete}
        proceedMessage={t('common.delete')}
        isLoading={isDeleting}
        icon={<WarningIcon aria-hidden='true' className={`${classes.warningIcon} align-self-center mb-2`} />}
        title={t('organizations.actions.deleteTitle', { name: organization.name })}
        message={detachedItems.length > 0
          ? t('organizations.actions.deleteMessage', { items: detachedItems.join(t('organizations.actions.detachJoin')) })
          : undefined}
      />
    </Drawer>
  )
}

OrganizationDrawer.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    coachesCount: PropTypes.number,
    certifiedCoachesCount: PropTypes.number,
    communitiesCount: PropTypes.number,
    communities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string.isRequired })),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
}

export default OrganizationDrawer
